import { Suspense, useRef } from "react";
import ParticipantCard from "./components/ParticipantCard.tsx";
import FormInput from "../../components/form/FormInput.tsx";
import { useTranslation } from "react-i18next";
import IconSearch from "@/assets/icons/search.svg?react";
import { useChallengeMembers } from "@/pages/challenges/api/challenge-members.api.ts";
import { ErrorBoundary } from "react-error-boundary";
import { ErrorHandler } from "@/components/error-hanlder/ErrorHandler.tsx";
import { logError } from "@/components/error-hanlder/error-handler.lib.ts";
import UserCardLoading from "@/pages/challenges/components/UserCardLoading.tsx";
import { useSuspenseInfiniteQuery } from "@tanstack/react-query";
import { useParams } from "react-router";
import { useDebounceValue } from "usehooks-ts";
import { useGetProfile } from "@/pages/profile/api";
import { ChallengeMember } from "@/pages/challenges/types";
import PageWrapper from "@/components/PageWrapper.tsx";
import IconCircle from "/src/assets/icons/alert-circle-linear.svg?react";
import { useState } from "react";
import IconX from "@/assets/icons/x-mark.svg?react";
import InfiniteScrollList, {
  checkFetchMore,
} from "@/components/base/InfiniteScrollList.tsx";
import { useInfiniteQueryFlatted } from "@/shared/hooks/useInfiniteQueryFlatted.ts";
import { useVirtualizer } from "@tanstack/react-virtual";
import { VirtualTile } from "@/components/base/VirtualTile.tsx";

const ChallengeSingleMembers = () => {
  const { t } = useTranslation();
  const [search, setSearch] = useState("");
  const [searchTerm, setSearchTerm] = useDebounceValue(search, 500);

  const handleClearSearch = () => {
    setSearch("");
    setSearchTerm("");
  };

  return (
    <PageWrapper back={true}>
      <div className="flex flex-col gap-3 container py-4">
        <FormInput
          className="!py-1.5 mb-2"
          placeholder={t("search_participants")}
          prefix={<IconSearch className="shrink-0 w-6 h-6 text-gray-1" />}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          suffix={
            search ? (
              <IconX
                className="cursor-pointer w-5 h-5 text-gray-1"
                onClick={handleClearSearch}
              />
            ) : undefined
          }
        />
        <MemberList searchTerm={searchTerm} />
      </div>
    </PageWrapper>
  );
};

interface MemberListProps {
  searchTerm?: string;
}

const MemberList = (props: MemberListProps) => {
  return (
    <ErrorBoundary FallbackComponent={ErrorHandler} onError={logError}>
      <Suspense
        fallback={
          <>
            {Array.from({ length: 3 }).map((_, idx) => (
              <UserCardLoading key={idx} />
            ))}
          </>
        }
      >
        <BaseChallengeSingleMembers {...props} />
      </Suspense>
    </ErrorBoundary>
  );
};

const BaseChallengeSingleMembers = (props: MemberListProps) => {
  const { searchTerm } = props;
  const { challengeId } = useParams();
  const { t } = useTranslation();
  const queryResult = useSuspenseInfiniteQuery(
    useChallengeMembers(challengeId, searchTerm)
  );

  const membersList = useInfiniteQueryFlatted(queryResult.data);
  const searchMode = !!searchTerm;

  return (
    <>
      <div className="flex items-center mb-2 justify-start gap-3 bg-[linear-gradient(83deg,_rgba(251,_199,_108,_0.10)_-31.43%,_rgba(235,_161,_50,_0.10)_100%)] py-2 px-3  rounded-lg">
        <IconCircle className="shrink-0 w-5 h-5" />
        <p className="text-xs leading-130 font-normal w-[90%] text-white">
          {t("participant_rule")}
        </p>
      </div>

      <InfiniteScrollList
        fetchNextPage={queryResult.fetchNextPage}
        canFetchMore={checkFetchMore(queryResult)}
        isFetchingNextPage={queryResult.isFetchingNextPage}
        placeholder={
          <div className="grid gap-3">
            {Array.from({ length: 1 }).map((_, idx) => (
              <UserCardLoading key={idx} />
            ))}
          </div>
        }
        className="grid gap-3"
      >
        <ChallengeMemberList members={membersList} searchMode={searchMode} />
      </InfiniteScrollList>
    </>
  );
};

interface ChallengeMemberListProps {
  members: ChallengeMember[];
  isCompleted?: boolean;
  searchMode?: boolean;
  showSeperator?: boolean;
  gift?: boolean;
}

export const ChallengeMemberList = (props: ChallengeMemberListProps) => {
  const { members, searchMode, showSeperator = true, isCompleted } = props;
  const { data: profile } = useGetProfile();

  const scrollRef = useRef<HTMLDivElement>(null);

  const sortedMembers = [...members].sort((a, b) => {
    if (a.gift_award && !b.gift_award) return -1;
    if (!a.gift_award && b.gift_award) return 1;
    return (a.position || 0) - (b.position || 0);
  });

  const lastGiftIndex = sortedMembers.reduce((lastIndex, member, index) => {
    return member.gift_award ? index : lastIndex;
  }, -1);

  const virtualizer = useVirtualizer({
    count: sortedMembers.length,
    getScrollElement: () => scrollRef.current,
    estimateSize: () => 56,
    overscan: 10,
  });

  return (
    <div ref={scrollRef}>
      <div
        style={{
          position: "relative",
          height: virtualizer.getTotalSize(),
        }}
      >
        {virtualizer.getVirtualItems().map((vItem) => {
          const member = sortedMembers[vItem.index];
          return (
            <VirtualTile
              key={vItem.index}
              virtualItem={vItem}
              ref={virtualizer.measureElement}
              className="pt-3 first:pt-0"
            >
              <ParticipantCard
                avatar={member.avatar ?? null}
                place={member.position}
                fullName={member.full_name}
                username={member.username}
                chance={member.opportunities}
                isPremium={member.is_premium}
                showSeperator={
                  !!member.gift_award &&
                  vItem.index === lastGiftIndex &&
                  !searchMode &&
                  showSeperator
                }
                gift={member.gift_award}
                isMe={profile?.id === member.user_id}
                isCompleted={!!isCompleted}
              />
            </VirtualTile>
          );
        })}
      </div>
    </div>
  );
};

export default ChallengeSingleMembers;
